import React, { useState } from 'react';
import { Box, Button, IconButton, Popover, TableCell, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import { useParams } from 'react-router-dom';
import InviteUserDialog from '../InviteUserDialog';
import ProgramDeletDialog from '../ProgramDeletDialog';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DeleteIcon } from '../../../../../../utils/Icons';
import { useAppDispatch } from '../../../../../../redux/hooks';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { IGroupInvitedUser, groupUsersActions } from '../../../../../../redux/slices/group-users/groupUsersSlice';

interface Props {
  data: IGroupInvitedUser;
  showGoalProgress?: boolean;
  showCompany?: boolean;
}

const ProgramInvitedUsers = ({ data, showGoalProgress, showCompany }: Props) => {
  const dispatch = useAppDispatch();
  const orgId = useParams().orgId || '';

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDelete, setDelete] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleOpen = (e: any) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const enrolledCount = data.groups?.[0]?.enrolledCount ?? '—';
  const primaryRole = data.role?.[0] ?? '—';

  const COLS = {
    checkbox: 22,
    name: 240,
    role: 240,
    lastLogin: 180,
    programs: 80,
    onboarding: 150,
    onboardedDate: 150,
    goalProgress: 140,
    experience: 150,
    meetings: 100,
    actions: 50
  };

  const handleRevokeInvitation = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.revokeGroupInvitation({
        orgId,
        invitedId: data.invitationId
      });

      if (response.status === 200) {
        dispatch(
          groupUsersActions.removeInvitedUser({
            invitationId: data.invitationId
          })
        );
        toast.success('Invitation revoked successfully');
      }
    } catch {
      toast.error('Invitation revoke failed');
    }
    setDeleteLoader(false);
  };

  return (
    <>
      <TableRow
        sx={{
          height: 65,
          borderBottom: '1px solid #2A2A2A',
          bgcolor: '#151515',
          color: '#FDFDFD',
          '&:hover': { bgcolor: '#151515' }
        }}
      >
        {/* 1 — checkbox */}
        <TableCell sx={{ width: COLS.checkbox }} />

        {/* 2 — NAME */}
        <TableCell sx={{ width: COLS.name }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
            {data.firstName} {data.lastName}
          </Typography>
        </TableCell>

        {/* 3 — ROLE */}
        <TableCell sx={{ width: COLS.role }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
            {_.startCase(data.role?.[0] ?? 'N/A')}
          </Typography>
        </TableCell>

        {/* 4 — LAST LOGIN */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.lastLogin }}>N/A</TableCell>

        {/* 5 — PROGRAMS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.programs, textAlign: 'center' }}>
          {data.groups?.[0]?.enrolledCount ?? '—'}
        </TableCell>

        {/* 6 — ONBOARDING STATUS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.onboarding }}>Invited</TableCell>

        {/* 7 — ONBOARDED DATE */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.onboardedDate, textAlign: 'center' }}>N/A</TableCell>

        {/* 8 — GOAL PROGRESS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.goalProgress, textAlign: 'center' }}>N/A</TableCell>

        {/* 9 — XP */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.experience, textAlign: 'center' }}>N/A</TableCell>

        {/* 10 — MEETINGS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.meetings, textAlign: 'center' }}>N/A</TableCell>

        {/* 11 — ACTIONS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.actions }}>
          <IconButton onClick={handleOpen}>
            <MoreVertIcon sx={{ color: '#FDFDFD' }} />
          </IconButton>
        </TableCell>
      </TableRow>

      <ProgramDeletDialog
        openDelete={openDelete}
        setDelete={setDelete}
        deleteLoader={deleteLoader}
        text="Are you sure you want to revoke the invitation?"
        handleDelete={handleRevokeInvitation}
        btnText="Yes, Revoke"
      />

      <InviteUserDialog
        handleClose={handleClose}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        data={data}
        type="invite"
      />
    </>
  );
};

export default ProgramInvitedUsers;
